/* Walking routes on the graph a model carries (scripts/convert-spot-model.ts writes it on the `nav`
 * node): one graph per drawn floor, the lift and each place attached to it. A route walks to the lift,
 * rides it, and walks on from the lift; on one floor it walks straight there. No Babylon here, so the
 * tests run it on the shipped model's data. */

interface NavFloor {
  /** The model's own floor id, e.g. f05. */
  id: string;
  /** Our floor number, the floor:<n> group it is drawn in. */
  floor: number;
  points: number[];
  edges: number[];
  lift: number | null;
  places: Record<string, number>;
}

export interface NavData {
  walkSpeed: number;
  floors: NavFloor[];
}

type Point = [number, number, number];

interface Leg {
  kind: 'walk' | 'lift';
  floor: number;
  /** Where the lift goes; null for a walk. */
  toFloor: number | null;
  metres: number;
  points: Point[];
}

export interface Route {
  metres: number;
  seconds: number;
  legs: Leg[];
}

/** Waiting for the lift and riding it: a fixed wait and a speed, both deliberately unhurried. */
const LIFT_WAIT_S = 20;
const LIFT_SPEED_M_S = 1.5;

export function parseNav(value: unknown): NavData | null {
  if (!value || typeof value !== 'object') return null;
  const data = value as Partial<NavData>;
  if (!Array.isArray(data.floors) || data.floors.length === 0) return null;
  return { walkSpeed: Number(data.walkSpeed) || 1.2, floors: data.floors };
}

const pointOf = (floor: NavFloor, index: number): Point => [
  floor.points[index * 3] as number,
  floor.points[index * 3 + 1] as number,
  floor.points[index * 3 + 2] as number,
];
const distance = (a: Point, b: Point): number => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

/** The shortest walk between two points of a floor's graph (Dijkstra), or null when they do not meet. */
function walk(floor: NavFloor, from: number, to: number): { metres: number; points: Point[] } | null {
  const count = floor.points.length / 3;
  const links: { to: number; cost: number }[][] = Array.from({ length: count }, () => []);
  for (let k = 0; k + 1 < floor.edges.length; k += 2) {
    const a = floor.edges[k] as number;
    const b = floor.edges[k + 1] as number;
    const cost = distance(pointOf(floor, a), pointOf(floor, b));
    links[a]?.push({ to: b, cost });
    links[b]?.push({ to: a, cost });
  }
  const best = new Float64Array(count).fill(Infinity);
  const previous = new Int32Array(count).fill(-1);
  const done = new Uint8Array(count);
  best[from] = 0;
  // A binary heap would be faster; a floor has a few thousand points, so a scan for the nearest open
  // point is a few milliseconds and keeps this obviously right.
  for (;;) {
    let at = -1;
    for (let i = 0; i < count; i++) {
      const cost = best[i] as number;
      if (!done[i] && cost < Infinity && (at < 0 || cost < (best[at] as number))) at = i;
    }
    if (at < 0 || at === to) break;
    done[at] = 1;
    for (const link of links[at] ?? []) {
      const cost = (best[at] as number) + link.cost;
      if (cost < (best[link.to] as number)) {
        best[link.to] = cost;
        previous[link.to] = at;
      }
    }
  }
  if (!Number.isFinite(best[to])) return null;
  const points: Point[] = [];
  for (let at = to; at >= 0; at = previous[at] as number) points.unshift(pointOf(floor, at));
  return { metres: best[to] as number, points };
}

export type RouteResult = Route | 'no_paths' | 'unknown_place' | 'no_route';

export function route(nav: NavData | null, from: string, to: string): RouteResult {
  if (!nav) return 'no_paths';
  const start = nav.floors.find((floor) => floor.places[from] !== undefined);
  const end = nav.floors.find((floor) => floor.places[to] !== undefined);
  if (!start || !end) return 'unknown_place';
  const startAt = start.places[from] as number;
  const endAt = end.places[to] as number;
  const legs: Leg[] = [];
  if (start === end) {
    const leg = walk(start, startAt, endAt);
    if (!leg) return 'no_route';
    legs.push({ kind: 'walk', floor: start.floor, toFloor: null, ...leg });
  } else {
    if (start.lift === null || end.lift === null) return 'no_route';
    const first = walk(start, startAt, start.lift);
    const last = walk(end, end.lift, endAt);
    if (!first || !last) return 'no_route';
    const liftFrom = pointOf(start, start.lift);
    const liftTo = pointOf(end, end.lift);
    legs.push(
      { kind: 'walk', floor: start.floor, toFloor: null, ...first },
      { kind: 'lift', floor: start.floor, toFloor: end.floor, metres: 0, points: [liftFrom, liftTo] },
      { kind: 'walk', floor: end.floor, toFloor: null, ...last },
    );
  }
  const walked = legs.reduce((sum, leg) => sum + leg.metres, 0);
  const lift = legs.find((leg) => leg.kind === 'lift');
  const rise = lift ? Math.abs((lift.points[1]?.[1] ?? 0) - (lift.points[0]?.[1] ?? 0)) : 0;
  return {
    metres: walked,
    seconds: walked / nav.walkSpeed + (lift ? LIFT_WAIT_S + rise / LIFT_SPEED_M_S : 0),
    legs,
  };
}
